import { createSlice } from '@reduxjs/toolkit';
import { DUMMY_RULES_BY_MERCHANT, DUMMY_REVERSE_RULES_BY_MERCHANT } from '../pages/merchants/d2c-allocation/dummyData/rules';
import { DUMMY_MERCHANTS_BY_ID } from '../pages/merchants/d2c-allocation/dummyData/merchants';
import { AC_META } from '../pages/merchants/d2c-allocation/dummyData/acMeta';

// Deep-clone seeded rules so editing one merchant never mutates the shared dummy fixtures
// (and so immer doesn't freeze the imported constants).
function cloneRules(rules) {
  return (rules || []).map((r) => ({
    ...r,
    zones: [...(r.zones || [])],
    payment: [...(r.payment || [])],
    qcModes: [...(r.qcModes || [])],
    activeConds: [...(r.activeConds || [])],
    serviceTypes: [...(r.serviceTypes || [])],
    customOrder: [...(r.customOrder || [])],
    condValues: { ...(r.condValues || {}) },
  }));
}

// Default AC enablement map: every parsed/curated AC enabled with whatever prepaid/cod the meta says.
function defaultAcEnabled() {
  const out = {};
  for (const ac of Object.keys(AC_META)) {
    const m = AC_META[ac];
    out[ac] = { prepaid: m.prepaid ? 1 : 0, cod: m.cod ? 1 : 0 };
  }
  return out;
}

const initialState = {
  // Which merchant is currently being edited. null = listing view.
  activeMerchantId: null,

  // Tab UI state
  activeTab: 'listing',

  // Edit-journey state
  allocationSource: null,        // one of TAXONOMY.allocationSources or null until picked
  approvalToken: null,           // set after OTP verify; cleared on publish
  currentUserRole: 'admin',      // staging default — replaced by real user role when wired
  platinumOtpEmail: 'platinum-approvals@prozo.com',

  // Rate Cards tab
  pricingSource: null,           // 'auto' | 'upload' | null
  merchantFwdRateCardName: null, // file name once uploaded
  merchantRvpRateCardName: null,
  merchantFwdRateCardRows: [],   // [{ parent, zone, weight, payment, revenue }] — parsed from uploaded file
  merchantRvpRateCardRows: [],   // same shape for reverse
  acEnabledSeedVersion: 0,       // incremented each time rows are uploaded
  acEnabledSeededVersion: 0,    // set to acEnabledSeedVersion after seeding completes; persists across tab navigation
  supplyOverrideName: null,

  // Manual allocation CSV upload (AllocationSourceModal: upload_manual / download_edit_upload).
  // When set, the raw 39-column allocation CSV is published verbatim as the csvBlob in
  // custom mode — bypassing rule-from-state regeneration. Cleared on merchant switch.
  uploadedAllocationCsv: null,   // raw CSV text of the uploaded 39-column allocation
  uploadedAllocationCsvName: null,

  // Forward allocation tab
  forwardMode: 'margin',         // 'margin' | 'cheapest' | 'custom'
  autoLoad: 'Regular',           // 'Regular' | 'Large' — UI load class selector
  autoLoadFallback: false,       // API autoLoad: cascade to next courier when P1 unavailable
  autoServiceTypes: [],          // subset of service type ids
  groupSameParent: true,         // global setting toggle
  customRules: [],               // mirrors §3.6 shape; seeded from DUMMY_RULES_BY_MERCHANT when merchant loaded
  defaultPriorityType: 'margin', // default fallback rule mode ('margin' | 'cost')
  skipMargin: false,             // advanced: allow loss-making couriers in priority list
  includeAdditional: true,       // advanced: include additional carriers beyond active allocation
  includeLossMaking: false,      // step-4 toggle: when OFF (default) the backend drops negative-margin couriers per lane at publish (highest_prozo_margin mode)

  // Reverse allocation tab
  rvpEnabled: true,              // master enable toggle for reverse pickup allocation
  reverseMode: 'custom',         // 'custom' only — Lowest Merchant Cost removed from reverse selection
  reverseRules: [],

  // Cross-cutting
  acEnabled: defaultAcEnabled(),
  acWeightBands: {},             // { [ac]: { min, max } } — per-AC weight band limits
  effectiveFrom: '',
  effectiveUntil: '',

  // UI ephemera
  helpDrawerOpen: false,
  emailTemplateDrawerOpen: false,

  // Modals
  otpModal: { open: false, title: '', body: '', purpose: null }, // purpose: 'edit_platinum' | 'publish_lossy'
  allocSourceModal: { open: false },
  publishModal: { open: false },
};

export const d2cAllocationSlice = createSlice({
  name: 'd2cAllocation',
  initialState,
  reducers: {
    setActiveMerchant: (s, a) => {
      const id = a.payload;
      const merchant = DUMMY_MERCHANTS_BY_ID[id];
      s.activeMerchantId = id;
      s.customRules = cloneRules(DUMMY_RULES_BY_MERCHANT[id]);
      s.reverseRules = cloneRules(DUMMY_REVERSE_RULES_BY_MERCHANT[id]);
      // Open the tab in the merchant's own mode so custom merchants land on their
      // rules and auto merchants land on the auto preview.
      s.forwardMode = merchant?.mode || 'margin';
      // Reverse has no margin mode — map margin → cheapest.
      s.reverseMode = 'custom';
      // Seed a sensible default service-type cascade so the auto-mode preview table
      // is populated immediately (the user can untick types).
      s.autoServiceTypes = [
        'SDD_FWD_REG',
        'SC_NDD_FWD_REG',
        'INTERCITY_NDD_REG',
        'FWD_AIR_REG',
        'FWD_SURFACE_REG',
      ];
      s.allocationSource = null;
      s.approvalToken = null;
      s.activeTab = 'merchant';
      s.pricingSource = null;
    },
    clearActiveMerchant: (s) => {
      s.activeMerchantId = null;
      s.activeTab = 'listing';
      s.customRules = [];
      s.reverseRules = [];
      s.approvalToken = null;
      s.allocationSource = null;
      s.uploadedAllocationCsv = null;
      s.uploadedAllocationCsvName = null;
    },
    setActiveTab: (s, a) => { s.activeTab = a.payload; },
    setAllocationSource: (s, a) => { s.allocationSource = a.payload; },
    setUploadedAllocationCsv: (s, a) => {
      s.uploadedAllocationCsv = a.payload?.csv ?? null;
      s.uploadedAllocationCsvName = a.payload?.name ?? null;
    },
    clearUploadedAllocationCsv: (s) => {
      s.uploadedAllocationCsv = null;
      s.uploadedAllocationCsvName = null;
    },
    setApprovalToken: (s, a) => { s.approvalToken = a.payload; },
    clearApprovalToken: (s) => { s.approvalToken = null; },
    setCurrentUserRole: (s, a) => { s.currentUserRole = a.payload; },
    setPlatinumOtpEmail: (s, a) => { s.platinumOtpEmail = a.payload; },

    setPricingSource: (s, a) => { s.pricingSource = a.payload; },
    setMerchantFwdRateCardName: (s, a) => { s.merchantFwdRateCardName = a.payload; },
    setMerchantRvpRateCardName: (s, a) => { s.merchantRvpRateCardName = a.payload; },
    setMerchantFwdRateCardRows: (s, a) => {
      s.merchantFwdRateCardRows = a.payload;
      s.acEnabledSeedVersion += 1; // signal AccountCodesTab to re-seed once
      s.acEnabled = {};             // clear so seed starts from scratch
    },
    setMerchantRvpRateCardRows: (s, a) => {
      s.merchantRvpRateCardRows = a.payload;
      s.acEnabledSeedVersion += 1;
      s.acEnabled = {};
    },
    setAcEnabledSeededVersion: (s, a) => { s.acEnabledSeededVersion = a.payload; },
    setSupplyOverrideName: (s, a) => { s.supplyOverrideName = a.payload; },

    setForwardMode: (s, a) => { s.forwardMode = a.payload; },
    setAutoLoad: (s, a) => { s.autoLoad = a.payload; },
    setAutoLoadFallback: (s, a) => { s.autoLoadFallback = !!a.payload; },
    setAutoServiceTypes: (s, a) => { s.autoServiceTypes = a.payload; },
    toggleAutoServiceType: (s, a) => {
      const id = a.payload;
      const idx = s.autoServiceTypes.indexOf(id);
      if (idx >= 0) s.autoServiceTypes.splice(idx, 1);
      else s.autoServiceTypes.push(id);
    },
    setGroupSameParent: (s, a) => { s.groupSameParent = a.payload; },
    setDefaultPriorityType: (s, a) => { s.defaultPriorityType = a.payload; },
    setSkipMargin: (s, a) => { s.skipMargin = a.payload; },
    setIncludeAdditional: (s, a) => { s.includeAdditional = a.payload; },
    setIncludeLossMaking: (s, a) => { s.includeLossMaking = !!a.payload; },

    addCustomRule: (s, a) => { s.customRules.unshift(a.payload); },
    updateCustomRule: (s, a) => {
      const idx = s.customRules.findIndex((r) => r.id === a.payload.id);
      if (idx >= 0) s.customRules[idx] = { ...s.customRules[idx], ...a.payload };
    },
    removeCustomRule: (s, a) => {
      s.customRules = s.customRules.filter((r) => r.id !== a.payload);
    },
    clearCustomRules: (s) => { s.customRules = []; },
    reorderCustomRules: (s, a) => {
      // payload: { from, to } — move a rule to a new priority position.
      const { from, to } = a.payload;
      if (from == null || to == null || from === to) return;
      if (from < 0 || to < 0 || from >= s.customRules.length || to >= s.customRules.length) return;
      const [moved] = s.customRules.splice(from, 1);
      s.customRules.splice(to, 0, moved);
    },

    setRvpEnabled: (s, a) => { s.rvpEnabled = a.payload; },
    setReverseMode: (s, a) => { s.reverseMode = a.payload; },
    addReverseRule: (s, a) => { s.reverseRules.unshift(a.payload); },
    updateReverseRule: (s, a) => {
      const idx = s.reverseRules.findIndex((r) => r.id === a.payload.id);
      if (idx >= 0) s.reverseRules[idx] = { ...s.reverseRules[idx], ...a.payload };
    },
    removeReverseRule: (s, a) => {
      s.reverseRules = s.reverseRules.filter((r) => r.id !== a.payload);
    },
    clearReverseRules: (s) => { s.reverseRules = []; },
    reorderReverseRules: (s, a) => {
      const { from, to } = a.payload;
      if (from == null || to == null || from === to) return;
      if (from < 0 || to < 0 || from >= s.reverseRules.length || to >= s.reverseRules.length) return;
      const [moved] = s.reverseRules.splice(from, 1);
      s.reverseRules.splice(to, 0, moved);
    },

    setAcEnabled: (s, a) => { s.acEnabled[a.payload.ac] = a.payload.flags; },
    setAcWeightBand: (s, a) => {
      // payload: { ac, key: 'min'|'max', value } — value '' clears that bound.
      const { ac, key, value } = a.payload;
      if (!s.acWeightBands[ac]) s.acWeightBands[ac] = {};
      if (value === '' || value == null) delete s.acWeightBands[ac][key];
      else s.acWeightBands[ac][key] = parseFloat(value);
    },
    setEffectiveFrom: (s, a) => { s.effectiveFrom = a.payload; },
    setEffectiveUntil: (s, a) => { s.effectiveUntil = a.payload; },

    setHelpDrawerOpen: (s, a) => { s.helpDrawerOpen = a.payload; },
    setEmailTemplateDrawerOpen: (s, a) => { s.emailTemplateDrawerOpen = a.payload; },

    openOtpModal: (s, a) => { s.otpModal = { open: true, ...a.payload }; },
    closeOtpModal: (s) => { s.otpModal = { open: false, title: '', body: '', purpose: null }; },

    openAllocSourceModal: (s) => { s.allocSourceModal.open = true; },
    closeAllocSourceModal: (s) => { s.allocSourceModal.open = false; },

    openPublishModal: (s) => { s.publishModal.open = true; },
    closePublishModal: (s) => { s.publishModal.open = false; },

    updateMerchantPickupPincodes: (s, a) => {
      s._pincodeOverrides = s._pincodeOverrides || {};
      s._pincodeOverrides[a.payload.merchantId] = a.payload.pincodes;
    },

    // ── API-driven hydration ──────────────────────────────────────────────────
    // Called by allocationApiSlice.onQueryStarted after API 2 responds.
    setActiveMerchantFromApi: (s, a) => {
      const m = a.payload;
      s.activeMerchantId = m.id;
      s.platinumOtpEmail = m.platinumOtpEmail || '';
      s.activeTab = 'merchant';
      s.approvalToken = null;
      s.allocationSource = null;
    },

    // Called by allocationApiSlice.onQueryStarted after API 3 (allocation rules) responds.
    hydrateAllocationFromApi: (s, a) => {
      const r = a.payload;
      if (!r) return;
      if (r.forwardMode)            s.forwardMode       = r.forwardMode.toLowerCase();
      if (r.reverseEnabled != null) s.rvpEnabled        = r.reverseEnabled;
      // API renamed autoLoad → autoFallback; keep backward compat for both field names.
      const fallback = r.autoFallback ?? r.autoLoad;
      if (fallback != null)         s.autoLoadFallback  = !!fallback;
      if (r.groupSameParent != null) s.groupSameParent  = r.groupSameParent;
      if (r.skipMargin != null)     s.skipMargin        = r.skipMargin;
      if (r.includeAdditional != null) s.includeAdditional = r.includeAdditional;
      if (r.includeLossMaking != null) s.includeLossMaking = !!r.includeLossMaking;
      if (r.defaultPriorityType)    s.defaultPriorityType = r.defaultPriorityType.toLowerCase();
      if (r.autoServiceTypes?.length) s.autoServiceTypes = r.autoServiceTypes;
      if (r.effectiveFrom)          s.effectiveFrom     = r.effectiveFrom;
      if (r.effectiveUntil)         s.effectiveUntil    = r.effectiveUntil;
      // Normalize API rule shape → UI shape:
      //  loadClass  → load
      //  couriers   → customOrder  (API uses 'couriers', UI uses 'customOrder')
      //  priorityType: 'COST'/'MARGIN'/'SPEED' → 'cost'/'margin'/'custom' (lowercase UI tokens)
      const normPriorityType = (pt) => {
        const u = String(pt || '').toUpperCase();
        if (u === 'COST' || u === 'CHEAPEST') return 'cost';
        if (u === 'SPEED' || u === 'CUSTOM')  return 'custom';
        return 'margin'; // default
      };
      if (r.customRules?.length) {
        s.customRules = r.customRules.map((rule) => ({
          ...rule,
          id:           rule.id || Date.now() + Math.random(),
          load:         rule.loadClass || rule.load || 'Regular',
          priorityType: normPriorityType(rule.priorityType),
          customOrder:  rule.customOrder?.length ? rule.customOrder
                      : rule.couriers?.length   ? rule.couriers
                      : [],
        }));
      }
      if (r.reverseRules?.length) {
        s.reverseRules = r.reverseRules.map((rule) => ({
          ...rule,
          id:           rule.id || Date.now() + Math.random(),
          priorityType: normPriorityType(rule.priorityType),
          customOrder:  rule.customOrder?.length ? rule.customOrder
                      : rule.couriers?.length   ? rule.couriers
                      : [],
        }));
      }
      // acEnabled — new unified shape: { ac: { prepaid, cod, zones: { LOCAL, REGIONAL, ... } } }
      // Extract prepaid/cod flags and store in the UI's payment-keyed shape.
      if (r.acEnabled && typeof r.acEnabled === 'object') {
        const next = {};
        for (const [ac, flags] of Object.entries(r.acEnabled)) {
          next[ac] = {
            prepaid: flags.prepaid ?? 1,
            cod:     flags.cod     ?? 1,
          };
        }
        s.acEnabled = next;
      }
    },
  },
});

export const {
  setActiveMerchant,
  clearActiveMerchant,
  setActiveTab,
  setAllocationSource,
  setUploadedAllocationCsv,
  clearUploadedAllocationCsv,
  setApprovalToken,
  clearApprovalToken,
  setCurrentUserRole,
  setPlatinumOtpEmail,
  setPricingSource,
  setMerchantFwdRateCardName,
  setMerchantRvpRateCardName,
  setMerchantFwdRateCardRows,
  setMerchantRvpRateCardRows,
  setAcEnabledSeededVersion,
  setSupplyOverrideName,
  setForwardMode,
  setAutoLoad,
  setAutoLoadFallback,
  setAutoServiceTypes,
  toggleAutoServiceType,
  setGroupSameParent,
  setDefaultPriorityType,
  setSkipMargin,
  setIncludeAdditional,
  setIncludeLossMaking,
  addCustomRule,
  clearCustomRules,
  updateCustomRule,
  removeCustomRule,
  reorderCustomRules,
  setRvpEnabled,
  setReverseMode,
  addReverseRule,
  updateReverseRule,
  removeReverseRule,
  clearReverseRules,
  reorderReverseRules,
  setAcEnabled,
  setAcWeightBand,
  setEffectiveFrom,
  setEffectiveUntil,
  setHelpDrawerOpen,
  setEmailTemplateDrawerOpen,
  openOtpModal,
  closeOtpModal,
  openAllocSourceModal,
  closeAllocSourceModal,
  openPublishModal,
  closePublishModal,
  updateMerchantPickupPincodes,
  setActiveMerchantFromApi,
  hydrateAllocationFromApi,
} = d2cAllocationSlice.actions;

export default d2cAllocationSlice.reducer;
